import { Navigate } from "react-router";
import * as Dtos from "../dtos/Tmd.dtos";
import type { AppRouteObject, RouteMetadataContext } from "./types";
import { lazyRoute } from "./lazyRoute";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";

const groupTopicPermissions: Record<string, Dtos.Permission | undefined> = {
    groups: Dtos.Permission.GroupView,
    group: Dtos.Permission.GroupView,
    "group-trials": Dtos.Permission.TrialGroupView,
    "group-personnels": Dtos.Permission.GroupPersonnelRoleView,
    "group-personnel": Dtos.Permission.GroupPersonnelRoleView,
    "group-items": Dtos.Permission.GroupItemView,
    "group-notes": Dtos.Permission.GroupNotesView,
    note: Dtos.Permission.GroupNotesView,
    "group-finance": Dtos.Permission.GroupFinanceView,
    "group-contract": Dtos.Permission.GroupContractView,
    "trial-group": Dtos.Permission.TrialGroupView,
    "trial-group-personnels": Dtos.Permission.TrialPersonnelRoleView,
    "trial-group-personnel": Dtos.Permission.TrialPersonnelRoleView,
    "trial-group-items": Dtos.Permission.TrialGroupItemView,
    "trial-group-laboratories": Dtos.Permission.TrialGroupLabView,
    "trial-group-laboratory": Dtos.Permission.TrialGroupLabView,
    "trial-group-laboratory-personnels": Dtos.Permission.TrialGroupLabView,
    "trial-group-laboratory-personnel": Dtos.Permission.TrialGroupLabView,
    "trial-group-laboratory-finance": Dtos.Permission.TrialGroupLaboratoryFinanceView,
    "trial-group-laboratory-contracts": Dtos.Permission.TrialGroupLaboratoryContractView,
    "trial-group-laboratory-contract": Dtos.Permission.TrialGroupLaboratoryContractView,
    "trial-group-finance": Dtos.Permission.TrialGroupFinanceView,
    "trial-group-contracts": Dtos.Permission.TrialGroupContractView,
    "trial-group-contract": Dtos.Permission.TrialGroupContractView,
    dashboard: Dtos.Permission.TrialGroupView,
};

const humanize = (path: string): string => path
    .replace(/^:/, "")
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, character => character.toUpperCase());

const routeValue = (path: string, context: RouteMetadataContext): string => path.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => context.params[key] ?? `:${key}`);

const groupLabel = (path: string, context: RouteMetadataContext): string => {
    if (path === ":groupId") return context.labels.groupName ?? "Group";
    if (path === ":trialId") return context.labels.trialName ?? "Trial";
    if (path === ":trialGroupId") return context.labels.trialGroupName ?? "Trial Group";
    if (path === ":trialPersonnelId" || path === ":trialGroupLaboratoryPersonnelId") return context.labels.personnelName ?? "Personnel";
    if (path === ":trialGroupLaboratoryId") return context.labels.laboratoryName ?? "Lab";
    if (path === ":trialGroupLaboratoryContractId" || path === ":trialGroupContractId") return context.labels.contractName ?? "Contract";
    if (path === ":noteId") return context.labels.noteName ?? "Note";
    if (path === "create") return "Create";
    return humanize(path);
};

const groupBreadcrumbIcon = (path: string): typeof FontAwesomeIcons.Duotone.HOME | undefined => {
    if (path === ":groupId") return FontAwesomeIcons.Duotone.GROUPARROWSROTATE;
    if (path === ":trialId" || path === ":trialGroupId") return FontAwesomeIcons.Duotone.DICEONE;
    if (path === "details") return FontAwesomeIcons.Duotone.CIRCLEINFO;
    if (path === "trials") return FontAwesomeIcons.Duotone.TRIAL;
    if (path === "personnel") return FontAwesomeIcons.Duotone.USER;
    if (path === "items") return FontAwesomeIcons.Duotone.I;
    if (path === "notes") return FontAwesomeIcons.Duotone.NOTESMEDICAL;
    if (path === "finance") return FontAwesomeIcons.Duotone.MONEYCHECKDOLLARPEN;
    if (path === "contract" || path === "contracts") return FontAwesomeIcons.Duotone.FILECONTRACT;
    return undefined;
};

const decorateGroupRoutes = (routes: AppRouteObject[], parentPath = ""): AppRouteObject[] => routes.map(route => {
    const path = route.path;
    const fullPath = path ? `${parentPath}/${path}`.replace(/\/+/g, "/") : parentPath;
    const topicKey = route.handle?.help?.topicKey;
    const title = route.handle?.title ?? (path ? ((path.startsWith(":") || path === "details" || path === "create")
        ? ((context: RouteMetadataContext) => groupLabel(path, context))
        : humanize(path)) : undefined);
    const accessPermission = topicKey ? groupTopicPermissions[topicKey] : undefined;
    const access = route.handle?.access !== undefined
        ? route.handle.access
        : accessPermission === undefined
            ? null
            : { permission: accessPermission, trialParam: fullPath.includes(":trialId") ? "trialId" : undefined };
    const breadcrumb = route.handle?.breadcrumb !== undefined
        ? route.handle.breadcrumb
        : !path || (path === "trialgroup") || (path === ":trialGroupId")
            ? null
            : {
                label: (context: RouteMetadataContext) => groupLabel(path, context),
                icon: groupBreadcrumbIcon(path),
                include: !route.index,
                url: (context: RouteMetadataContext) => {
                    const resolved = routeValue(fullPath, context);
                    if (path.startsWith(":")) return `${resolved}/details`;
                    return resolved;
                },
            };
    const navigation = route.handle?.navigation;
    return {
        ...route,
        handle: { ...route.handle, title, access, breadcrumb, navigation },
        children: route.children ? decorateGroupRoutes(route.children, fullPath) : undefined,
    } as AppRouteObject;
});

const rawGroupRoutes = {
    path: "groups",
    handle: {
        title: "Groups",
        help: null,
        breadcrumb: { label: "Groups", icon: FontAwesomeIcons.Duotone.GROUP, url: "/groups" },
    },
    children: [
        {
            index: true,
            lazy: lazyRoute(() => import("../containers/Groups/Groups/GroupsPage")),
            handle: { help: { topicKey: "groups" } }
        },
        {
            path: "create",
            lazy: lazyRoute(() => import("../containers/Groups/Group/GroupPage"), { mode: "create" }),
            handle: { help: { topicKey: "group" } }
        },
        {
            path: ":groupId",
            handle: { help: null },
            children: [
                {
                    index: true,
                    element: <Navigate replace to="details" />,
                    handle: { help: null }
                },
                {
                    path: "details",
                    lazy: lazyRoute(() => import("../containers/Groups/Group/GroupPage"), { mode: "edit" }),
                    handle: { help: { topicKey: "group" } }
                },
                {
                    path: "trials",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Groups/GroupTrials/GroupTrialsPage")),
                            handle: { help: { topicKey: "group-trials" } }
                        },
                        {
                            path: ":trialId",
                            handle: { help: null },
                            children: [
                                {
                                    path: "trialgroup",
                                    handle: { help: null },
                                    children: [
                                        {
                                            path: ":trialGroupId",
                                            handle: { help: { topicKey: "dashboard" } },
                                            children: [
                                                {
                                                    index: true,
                                                    element: (
                                                        <Navigate replace to="details" />
                                                    ),
                                                    handle: { help: null }
                                                },
                                                {
                                                    path: "details",
                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPage/TrialGroupPageSWR")),
                                                    handle: { help: { topicKey: "trial-group" } }
                                                },
                                                {
                                                    path: "personnel",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPersonnels/TrialGroupPersonnelsPage")),
                                                            handle: { help: { topicKey: "trial-group-personnels" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPersonnel/TrialGroupPersonnelPage")),
                                                            handle: { help: { topicKey: "trial-group-personnel" } }
                                                        },
                                                        {
                                                            path: ":trialPersonnelId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupPersonnel/TrialGroupPersonnelPage")),
                                                                        handle: { help: { topicKey: "trial-group-personnel" } }
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: "items",
                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupItems/TrialGroupItemsPageSWR")),
                                                    handle: { help: { topicKey: "trial-group-items" } }
                                                },
                                                {
                                                    path: "labs",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratories/TrialGroupLaboratoriesPage")),
                                                            handle: { help: { topicKey: "trial-group-laboratories" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratory/TrialGroupLaboratoryPage"), { kind: "trialGroup", routeOrientation: "group", mode: "create" }),
                                                            handle: { help: { topicKey: "trial-group-laboratory" } }
                                                        },
                                                        {
                                                            path: ":trialGroupLaboratoryId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratory/TrialGroupLaboratoryPage"), { kind: "trialGroup", routeOrientation: "group", mode: "edit" }),
                                                                        handle: { help: { topicKey: "trial-group-laboratory" } }
                                                                    },
                                                                    {
                                                                        path: "personnel",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryPersonnels/TrialGroupLaboratoryPersonnelsPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-personnels" } }
                                                                                },
                                                                                {
                                                                                    path: "create",
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryPersonnel/TrialGroupLaboratoryPersonnelPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                },
                                                                                {
                                                                                    path: ":trialGroupLaboratoryPersonnelId",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element:
                                                                                                    (
                                                                                                        <Navigate replace to="details" />
                                                                                                    ),
                                                                                                    handle: { help: null }
                                                                                            },
                                                                                            {
                                                                                                path: "details",
                                                                                                lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryPersonnel/TrialGroupLaboratoryPersonnelPage")),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-personnel" } }
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                    {
                                                                        path: "finance",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryFinance/TrialGroupLaboratoryFinancePage")),
                                                                        handle: { help: { topicKey: "trial-group-laboratory-finance" } }
                                                                    },
                                                                    {
                                                                        path: "contracts",
                                                                        handle: { help: null },
                                                                        children:
                                                                            [
                                                                                {
                                                                                    index: true,
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryContracts/TrialGroupLaboratoryContractsPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-contracts" } }
                                                                                },
                                                                                {
                                                                                    path: "create",
                                                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryContract/TrialGroupLaboratoryContractPage")),
                                                                                    handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                },
                                                                                {
                                                                                    path: ":trialGroupLaboratoryContractId",
                                                                                    handle: { help: null },
                                                                                    children:
                                                                                        [
                                                                                            {
                                                                                                index: true,
                                                                                                element:
                                                                                                    (
                                                                                                        <Navigate replace to="details" />
                                                                                                    ),
                                                                                                    handle: { help: null }
                                                                                            },
                                                                                            {
                                                                                                path: "details",
                                                                                                lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupLaboratoryContract/TrialGroupLaboratoryContractPage")),
                                                                                                handle: { help: { topicKey: "trial-group-laboratory-contract" } }
                                                                                            },
                                                                                        ],
                                                                                },
                                                                            ],
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: "finance",
                                                    lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupFinance/TrialGroupFinancePage")),
                                                    handle: { help: { topicKey: "trial-group-finance" } }
                                                },
                                                {
                                                    path: "contracts",
                                                    handle: { help: null },
                                                    children: [
                                                        {
                                                            index: true,
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupContracts/TrialGroupContractsPage")),
                                                            handle: { help: { topicKey: "trial-group-contracts" } }
                                                        },
                                                        {
                                                            path: "create",
                                                            lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupContract/TrialGroupContractPage")),
                                                            handle: { help: { topicKey: "trial-group-contract" } }
                                                        },
                                                        {
                                                            path: ":trialGroupContractId",
                                                            handle: { help: null },
                                                            children:
                                                                [
                                                                    {
                                                                        index: true,
                                                                        element:
                                                                            (
                                                                                <Navigate replace to="details" />
                                                                            ),
                                                                            handle: { help: null }
                                                                    },
                                                                    {
                                                                        path: "details",
                                                                        lazy: lazyRoute(() => import("../containers/TrialGroup/TrialGroupContract/TrialGroupContractPage")),
                                                                        handle: { help: { topicKey: "trial-group-contract" } }
                                                                    },
                                                                ],
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "notes",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Groups/GroupNotes/GroupNotesPage")),
                            handle: { help: { topicKey: "group-notes" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "group", mode: "create" }),
                            handle: { help: { topicKey: "note" } }
                        },
                        {
                            path: ":noteId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Notes/Note/NotePageSWR"), { kind: "group", mode: "edit" }),
                                    handle: { help: { topicKey: "note" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "personnel",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Groups/GroupPersonnels/GroupPersonnelsPage")),
                            handle: { help: { topicKey: "group-personnels" } }
                        },
                        {
                            path: "create",
                            lazy: lazyRoute(() => import("../containers/Groups/GroupPersonnel/GroupPersonnelPage")),
                            handle: { help: { topicKey: "group-personnel" } }
                        },
                        {
                            path: ":trialPersonnelId",
                            handle: { help: null },
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate replace to="details" />
                                    ),
                                    handle: { help: null }
                                },
                                {
                                    path: "details",
                                    lazy: lazyRoute(() => import("../containers/Groups/GroupPersonnel/GroupPersonnelPage")),
                                    handle: { help: { topicKey: "group-personnel" } }
                                },
                            ],
                        },
                    ],
                },
                {
                    path: "contracts",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Groups/GroupContract/GroupContractPage")),
                            handle: { help: { topicKey: "group-contract" } }
                        },
                    ],
                },
                {
                    path: "finances",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Groups/GroupFinance/GroupFinancePage")),
                            handle: { help: { topicKey: "group-finance" } }
                        },
                    ],
                },
                {
                    path: "items",
                    handle: { help: null },
                    children: [
                        {
                            index: true,
                            lazy: lazyRoute(() => import("../containers/Groups/GroupItems/GroupItemsPageSWR")),
                            handle: { help: { topicKey: "group-items" } }
                        },
                    ],
                },
            ],
        },
    ],
} satisfies AppRouteObject;

export const groupRoutes = decorateGroupRoutes([rawGroupRoutes])[0];

